import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import Button from '../Button';
import { formatColumnName } from './dataProcessing';

/**
 * Modal for configuring pivot table options
 */
const PivotConfigModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  columns = [], 
  schema,
  initialConfig = {} 
}) => {
  // Fixed initialization to protect against null initialConfig
  const [rowField, setRowField] = useState(initialConfig && initialConfig.rowField || '');
  const [columnField, setColumnField] = useState(initialConfig && initialConfig.columnField || '');
  const [valueField, setValueField] = useState(initialConfig && initialConfig.valueField || '');
  const [aggregationType, setAggregationType] = useState(initialConfig && initialConfig.aggregationType || 'sum');
  const [error, setError] = useState('');

  useEffect(() => {
    // Set default fields if nothing is selected and columns are available
    if (columns.length >= 3 && !rowField && !columnField && !valueField) {
      // Find date columns for row field
      const dateColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        if (!field) return false;
        
        // Use detectedType if available, otherwise fall back to original type checking
        return field.detectedType === 'DATE' || 
              field.type?.includes('DATE') || 
              field.type?.includes('TIMESTAMP');
      });

      // Find string columns for column field
      const stringColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        if (!field) return false;
        
        // Exclude detected date columns from string columns
        return field.detectedType !== 'DATE' && 
              (field.detectedType === 'STRING' || field.type === 'STRING');
      });

      // Find numeric columns for value field
      const numericColumns = columns.filter(col => {
        const field = schema?.fields?.find(f => f.name === col);
        if (!field) return false;
        
        return field.detectedType === 'NUMERIC' || 
              ['INTEGER', 'FLOAT', 'NUMERIC'].includes(field.type?.toUpperCase() || '');
      });
    
    // Use first date column for row field
    const rowColumn = dateColumns[0] || columns[0];
    setRowField(rowColumn);
    
    // Use first string column (not used for row) for column field
    const columnColumn = stringColumns.find(col => col !== rowColumn) || 
                        columns.find(col => col !== rowColumn) || 
                        columns[1] || 
                        columns[0];
    setColumnField(columnColumn);
    
    // Use first numeric column (not used for row/column) for value field
    const valueColumn = numericColumns.find(col => col !== rowColumn && col !== columnColumn) || 
                       columns.find(col => col !== rowColumn && col !== columnColumn) || 
                       columns[2] || 
                       columns[0];
    setValueField(valueColumn);
  } else if (initialConfig && initialConfig.rowField && initialConfig.columnField && initialConfig.valueField) {
    // Use provided initial configuration
    setRowField(initialConfig.rowField);
    setColumnField(initialConfig.columnField);
    setValueField(initialConfig.valueField);
    // Fix: Use safe check for aggregationType
    setAggregationType((initialConfig && initialConfig.aggregationType) || 'sum');
  }
}, [columns, schema, initialConfig, rowField, columnField, valueField]);

  // Reset error on field changes
  useEffect(() => {
    setError('');
  }, [rowField, columnField, valueField, aggregationType]);

  const handleApply = () => {
    // Validate selections
    if (!rowField) {
      setError('Please select a row field');
      return;
    }
    
    if (!columnField) {
      setError('Please select a column field');
      return;
    }
    
    if (!valueField) {
      setError('Please select a value field');
      return;
    }
    
    if (rowField === columnField) {
      setError('Row and column fields must be different');
      return;
    }
    
    onApply({
      rowField,
      columnField,
      valueField,
      aggregationType
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Configure Pivot Table</h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Row Field Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Row Field
              </label>
              <select
                value={rowField}
                onChange={(e) => setRowField(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="">Select field...</option>
                {columns.map(column => (
                  <option key={`row-${column}`} value={column}>
                    {formatColumnName(column)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                These values will appear as rows in the pivot table
              </p>
            </div>
            
            {/* Column Field Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Column Field
              </label>
              <select
                value={columnField}
                onChange={(e) => setColumnField(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="">Select field...</option>
                {columns.map(column => (
                  <option key={`col-${column}`} value={column}>
                    {formatColumnName(column)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                These values will appear as columns in the pivot table
              </p>
            </div>
            
            {/* Value Field Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value Field
              </label>
              <select
                value={valueField}
                onChange={(e) => setValueField(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="">Select field...</option>
                {columns.map(column => (
                  <option key={`val-${column}`} value={column}>
                    {formatColumnName(column)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Values from this field will be aggregated
              </p>
            </div>
            
            {/* Aggregation Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aggregation Type
              </label>
              <select
                value={aggregationType}
                onChange={(e) => setAggregationType(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="sum">Sum</option>
                <option value="avg">Average</option>
                <option value="count">Count</option>
                <option value="min">Minimum</option>
                <option value="max">Maximum</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                How to combine values in each cell
              </p>
            </div>
            
            {/* Error message */}
            {error && (
              <div className="flex items-start bg-red-50 text-red-700 p-3 rounded-md">
                <Info size={16} className="mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <Button 
            onClick={onClose}
            className="text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            className="bg-primary text-white hover:bg-primary-dark"
          >
            Apply Pivot
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PivotConfigModal;